import React, { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, Pause, Square } from "lucide-react";
import { T, useLanguage } from "../context/LanguageContext";

// Free browser text-to-speech (Web Speech API) — no API cost, 45+ languages.
export const ListenButton = ({ targetId }) => {
  const { lang } = useLanguage();
  const [state, setState] = useState("idle");
  const [speed, setSpeed] = useState(1);
  const queueRef = useRef({ chunks: [], index: 0, active: false });

  const stop = useCallback(() => {
    queueRef.current.active = false;
    window.speechSynthesis?.cancel();
    setState("idle");
  }, []);

  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);
  useEffect(() => { stop(); }, [lang, stop]);

  const speakNext = useCallback((rate) => {
    const q = queueRef.current;
    if (!q.active || q.index >= q.chunks.length) {
      q.active = false;
      setState("idle");
      return;
    }
    const utter = new SpeechSynthesisUtterance(q.chunks[q.index]);
    utter.rate = rate;
    utter.lang = lang;
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find((v) => v.lang?.toLowerCase().startsWith(lang)) || null;
    if (voice) utter.voice = voice;
    utter.onend = () => { q.index += 1; speakNext(rate); };
    utter.onerror = () => { q.active = false; setState("idle"); };
    window.speechSynthesis.speak(utter);
  }, [lang]);

  const toggle = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (state === "playing") {
      synth.pause();
      setState("paused");
      return;
    }
    if (state === "paused") {
      synth.resume();
      setState("playing");
      return;
    }
    const el = document.getElementById(targetId);
    if (!el) return;
    const text = el.innerText.replace(/\s+/g, " ").slice(0, 12000);
    const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
    const chunks = [];
    let buf = "";
    for (const s of sentences) {
      if ((buf + s).length > 200 && buf) { chunks.push(buf); buf = s; }
      else buf += s;
    }
    if (buf) chunks.push(buf);
    synth.cancel();
    queueRef.current = { chunks, index: 0, active: true };
    setState("playing");
    speakNext(speed);
  };

  const cycleSpeed = () => {
    const next = speed === 1 ? 1.25 : speed === 1.25 ? 1.5 : 1;
    setSpeed(next);
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        data-testid="listen-page-button"
        onClick={toggle}
        className="inline-flex items-center gap-2 rounded-full bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0040CC]"
      >
        {state === "playing" ? <Pause className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        {state === "playing" ? <T>Pause</T> : state === "paused" ? <T>Resume</T> : <T>Listen to this page</T>}
        <span aria-hidden>›</span>
      </button>
      {state !== "idle" && (
        <button data-testid="listen-stop-button" onClick={stop} className="rounded-full border border-slate-300 bg-white p-2 text-slate-700 hover:border-red-400 hover:text-red-500">
          <Square className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        data-testid="listen-speed-button"
        onClick={cycleSpeed}
        className="rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:border-[#0055FF] hover:text-[#0055FF]"
      >
        {speed}x
      </button>
    </div>
  );
};
